import React, { useEffect, useState } from 'react';
import './RegulatoryBodyReport.scss';
import PropTypes from 'prop-types';

import Paper from '@mui/material/Paper';
import { DataGrid, gridSortedRowIdsSelector, GridToolbarContainer, useGridApiContext } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';

import DeviceLink from 'src/component/UI/DeviceLink';
import { getReportPDFLoading, getReportData, getReportLoading } from 'src/module/device/selector';
import { Box, Typography } from '@mui/material';
import { formatDate, usePDFServices } from 'src/utils/utils';
import LogoSpinner from 'src/component/UI/LogoSpinner';
import moment from 'moment';

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import classNames from 'classnames';
import { generateTestingReportPdf } from 'src/module/device/action';
import LoadingSpinner from 'src/component/UI/LoadingSpinner';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';
import { isWhitelabel } from 'src/utils/utils';


export default function RegulatoryBodyReport ({ filters }) {
  const { t } = useTranslation();
  const data = useSelector(getReportData('regulator'));
  const loading = useSelector(getReportLoading);
  const pdfLoading = useSelector(getReportPDFLoading);
  const displayedIcon = pdfLoading ? (<LoadingSpinner color={variables.primaryLight}/>) : <FileDownloadOutlinedIcon className='custom-toolbar-download-icon' />;
  const [usePDFServicesValue, setUsePDFServicesValue] = useState<boolean | null>(null);
  const [usePDFServicesLoading, setUsePDFServicesLoading] = useState(true);
  const [hasRun, setHasRun] = useState(false);
  const whitelabel = isWhitelabel();

  // Track if report has been run (not loading and data exists)
  useEffect(() => {
    if (!loading && data !== null && data !== undefined) {
      setHasRun(true);
    }
  }, [loading, data]);

  useEffect(() => {
    async function loadData () {
      const result = await usePDFServices();
      setUsePDFServicesValue(result);
      setUsePDFServicesLoading(false);
    }
    loadData();
  }, []);

  const columns = [
    {
      field: 'unit',
      headerName: t('common.unit'),
      minWidth: 50,
      flex: 1
    },
    {
      field: 'parent_tag',
      headerName: t('regulatoryReport.controller'),
      minWidth: 100,
      renderCell: (params) =>  (<DeviceLink label={params.value} controllerTag={params.value} />),
      flex: 1
    },
    {
      field: 'tag',
      headerName: t('regulatoryReport.circuit'),
      minWidth: 100,
      renderCell: (params) =>  (<DeviceLink label={params.value} circuitTag={params.value} controllerTag={params.row.parent_tag}/>),
      flex: 1
    },
    {
      field: 'line_description',
      headerName: t('regulatoryReport.lineDescription'),
      minWidth: 125,
      flex: 1
    },
    {
      field: 'measured_current',
      headerName: t('regulatoryReport.measuredCurrent'),
      minWidth: 125,
      flex: 1
    },
    {
      field: 'measured_voltage',
      headerName: t('regulatoryReport.measuredVoltage'),
      minWidth: 125,
      flex: 1
    },
    {
      field: 'circuit_inspection_passed',
      headerName: t('regulatoryReport.circuitInspection'),
      minWidth: 125,
      renderCell: (params) => {
        if (params.value) {
          return t('regulatoryReport.passed');
        }
        if (params.row.circuit_function_created_at) {
          return t('common.failed');
        }
        return '';
      },
      flex: 1
    },
    {
      field: 'circuit_function_created_at',
      headerName: t('regulatoryReport.inspectionDate'),
      minWidth: 140,
      valueFormatter: (params) => params.value ? formatDate(params.value) : '',
      flex: 1
    },
    {
      field: 'insulation_passed',
      headerName: t('regulatoryReport.insulationInspection'),
      minWidth: 150,
      renderCell: (params) => {
        if (params.value) {
          return t('regulatoryReport.passed');
        }
        if (params.row.insulation_created_at) {
          return t('common.failed');
        }
        return '';
      },
      flex: 1
    },
    {
      field: 'insulation_created_at',
      headerName: t('regulatoryReport.insulationDate'),
      minWidth: 140,
      type: 'date',
      valueFormatter: (params) => params.value ? formatDate(params.value) : '',
      flex: 1
    },
  ];

  const getUnfilteredRows = ({ apiRef }) => gridSortedRowIdsSelector(apiRef);
  const dispatch = useDispatch();

  const handlePdfGenerationSubmit = () => {
    if (!pdfLoading) {
      dispatch(generateTestingReportPdf('regulatory', filters.selectedFilters, null, filters.startDate ? moment(filters.startDate).format('YYYY-MM-DD') : null, filters.endDate ? moment(filters.endDate).format('YYYY-MM-DD') : null, whitelabel ? 'yes' : 'no'));
    }
  };

  const CustomToolbar = () => {
    const apiRef = useGridApiContext();

    const handleExport = (options) => {
      if (apiRef && apiRef.current) {
        // Get the CSV string from DataGrid
        const csvString = apiRef.current.getDataAsCsv({
          ...options,
          escapeFormulas: true,
          delimiter: ',',
          shouldAppendQuotes: true
        });

        // Add UTF-8 BOM to ensure proper encoding in Excel
        const BOM = '\uFEFF';
        const csvWithBOM = BOM + csvString;

        // Create blob with UTF-8 encoding
        const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `regulatory-report-${new Date().toISOString().split('T')[0]}.csv`);
        link.click();
        URL.revokeObjectURL(url);
      }
    };

    return (
      <GridToolbarContainer>
        <Box sx={{ flexGrow: 1 }} /> {/* Spacer to push the export icon to the right */}

        <div
          className='custom-toolbar-option'
          onClick={() => handleExport({ getRowsToExport: getUnfilteredRows })}
        >
          <FileDownloadOutlinedIcon className='custom-toolbar-download-icon'/>
          <div className={classNames('custom-toolbar-text', 'custom-toolbar-text-reg ')}>{t('regulatoryReport.downloadCsv')}</div>
        </div>
        {!usePDFServicesLoading && usePDFServicesValue && (
          <div
            className='custom-toolbar-option'
            onClick={handlePdfGenerationSubmit}
          >
            <div className={classNames('custom-toolbar-text', 'custom-toolbar-text-reg ')}>
              {displayedIcon}
              {t('regulatoryReport.downloadPdf')}
            </div>
          </div>
        )}
      </GridToolbarContainer>
    );
  };
  return (
    <>
      {loading && <div style={{ width: '300px', height: '500px', margin: 'auto', paddingTop: '200px' }}>
        <LogoSpinner size={200} />
      </div> }

      {!loading && (
        <Paper style={{ marginTop: '20px' }}>
          <h2>{t('regulatoryReport.title')}</h2>

          {(!data || data.size === 0) ? (
            hasRun ? (
              <Box sx={{ padding: '40px', textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  {t('reports.noResults')}
                </Typography>
              </Box>
            ) : null
          ) : (
            <Box sx={{ height: 400, width: '100%' }}>
              <DataGrid
                rows={data?.toJS()}
                columns={columns}
                sx={{
                  border: 'none', // Removes the outer border
                  '& .MuiDataGrid-columnSeparator': {
                    display: 'none', // Removes the separator lines between column headers
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                  },
                }}

                density='compact'
                components={{
                  Toolbar: CustomToolbar,
                }}
              />
            </Box>
          )}
        </Paper>
      )}
    </>
  );
}

RegulatoryBodyReport.propTypes = {
  filters: PropTypes.object
};
RegulatoryBodyReport.defaultProps = {

};
